import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { Logger } from '../core/logger.service';

import { OrderDto } from './common/order-dto';
import { OrderFilterType } from './common/order-list/order-list.component';
import { SubscriptionDataDto } from './access-config.component';
import { PageEvent } from '@angular/material/paginator';
import { ApiService } from '../core/api/api.service';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { LoaderService } from '../shared/loader/loader.service';
import { BaseSubActionDto } from '../core/dto/sub-config-action-dto';

const routes = {
  subFindSubscription: '/api/subFindSubscription',
  usrFindRegisteredUser: '/api/usrFindRegisteredUser',
  subCreateAccess: 'api/subCreateAccess',
  subSearchSubscriptions: '/api/subSearchSubscriptions',
  apiCreateOrder: '/api/orders',
  subUpdateOrder: '/api/subUpdateOrder',
  usrSendMail: '/api/usrSendMail',
  subAcceptContract: '/api/subAcceptContract',
  subAssignSubscription: '/api/subAssignSubscription',
};

const logger: Logger = new Logger('registeredUserService');

@Injectable({ providedIn: 'root' })
export class AccessConfigService {
  // inject() al posto di constructor(private http: HttpClient)
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly loaderService = inject(LoaderService);

  readonly pageEvent = signal<PageEvent | undefined>(undefined);

  public findSubscription(
    email: string,
    subscriptionId: string,
    randomId: string,
  ): Observable<SubscriptionDataDto> {
    return this.http
      .post<SubscriptionDataDto>(
        routes.subFindSubscription,
        { email, subscriptionId, randomId },
        {
          headers: new HttpHeaders({ Authorization: '' }),
        },
      )
      .pipe(
        catchError((error) => {
          logger.error(error);
          return throwError(() => error);
        }),
      );
  }

  public createAccess(
    subscriptionId: string,
    randomId: string,
    access: any,
    operator = '',
    sendMail = false,
  ): Observable<any> {
    return this.http
      .post<any>(
        routes.subCreateAccess,
        { subscriptionId, randomId, sendMail, operator, access },
        {
          headers: new HttpHeaders({ Authorization: '' }),
        },
      )
      .pipe(
        catchError((error) => {
          logger.error(error);
          return throwError(() => error);
        }),
      );
  }

  public createOrder(
    order: OrderDto,
    days: number,
    operator: string,
    sendMail: boolean,
  ): Observable<any> {
    return this.http
      .post<any>(
        routes.apiCreateOrder,
        {
          subscriptionId: order.subscriptionId,
          userType: order.userType,
          mechCode: order.mechCode,
          planId: order.planId,
          origin: order.origin,
          backofficeOrderId: order.backofficeOrderId,
          networkUserId: order.networkUserId,
          userEmail: order.userEmail,
          fullName: order.fullName,
          licenseType: order.licenseType,
          domainLics: order.domainLics.toString(),
          slotCount: order.slotCount.toString(),
          startDate: order.startDate,
          days: days.toString(),
          operator,
          sendMail,
        },
        { withCredentials: false },
      )
      .pipe(
        catchError((error) => {
          logger.error(error);
          return throwError(() => error);
        }),
      );
  }

  public searchSubscriptions(
    search = '',
    includeExpired = false,
    page = 1,
    perPage = 10,
    orderBy: any = '',
    orderAsc: any = '',
    filter = OrderFilterType.ALL,
  ): Observable<any> {
    return this.http
      .post<any>(routes.subSearchSubscriptions, {
        search,
        includeExpired,
        page,
        perPage,
        orderBy,
        orderAsc,
        filter,
      })
      .pipe(
        catchError((error) => {
          logger.error(error);
          return throwError(() => error);
        }),
      );
  }

  public updateOrder(
    subscriptionId: string,
    randomId: string,
    userEmail: string,
    fullName: string,
    startDate: string,
    expireDate: string,
    origin: string,
    suspended: boolean,
    notes: string,
    operator: string,
    slotCount: number,
    domainLics: number,
    mechCode: string,
    planId: string,
    ocrMathScanQuota: number,
    ocrMathScansPolicy: string,
    userType: string,
  ): Observable<any> {
    return this.http
      .post<any>(routes.subUpdateOrder, {
        subscriptionId,
        randomId,
        userEmail,
        fullName,
        startDate,
        expireDate,
        origin,
        suspended,
        notes,
        slotCount: slotCount.toString(),
        domainLics,
        operator,
        userType,
        mechCode,
        planId,
        ocrMathScanQuota,
        ocrMathScanPolicy: ocrMathScansPolicy,
      })
      .pipe(
        catchError((error) => {
          logger.error(error);
          return throwError(() => error);
        }),
      );
  }

  public sendMail(
    subscriptionId: string,
    randomId: string,
    mailTo: string,
    fullName: string,
    toEndUser = false,
  ): Observable<any> {
    return this.http
      .post<any>(routes.usrSendMail, { subscriptionId, randomId, mailTo, fullName, toEndUser })
      .pipe(
        catchError((error) => {
          logger.error(error);
          return throwError(() => error);
        }),
      );
  }

  public acceptContract(subscriptionId: string, randomId: string): Observable<any> {
    return this.http
      .post<any>(
        routes.subAcceptContract,
        { subscriptionId, randomId },
        {
          headers: new HttpHeaders({ Authorization: '' }),
        },
      )
      .pipe(
        catchError((error) => {
          logger.error(error);
          return throwError(() => error);
        }),
      );
  }

  public assignSubscription(
    subscriptionId: string,
    action: string,
    subscriptionIdToRenew: string,
  ): Observable<any> {
    return this.http
      .post<any>(
        routes.subAssignSubscription,
        { subscriptionId, action, subscriptionIdToRenew },
        {
          headers: new HttpHeaders({ Authorization: '' }),
        },
      )
      .pipe(
        catchError((error) => {
          logger.error(error);
          return throwError(() => error);
        }),
      );
  }

  public async doSubActions(
    subscriptionId: string,
    randomId: string,
    actions: Array<BaseSubActionDto>,
  ): Promise<void> {
    if (!actions?.length) return;

    this.loaderService.startLoading('generic', 'PLEASE_WAIT');

    try {
      const res = await firstValueFrom(
        this.apiService.subConfigActions(
          this.authenticationService.getUserIdentifier(),
          subscriptionId,
          randomId,
          actions,
        ),
      );

      this.loaderService.stopLoading('generic');
      console.log(
        `subConfigActions result: ok=${res.ok} message=${res.message} error=${res.error}`,
      );
    } catch (error) {
      this.loaderService.stopLoading('generic');
      console.error('subConfigActions error:', error);
      throw error;
    }
  }
}
